<template>
  <v-dialog v-model="dialog" persistent max-width="400px">
    <template v-slot:activator="{ on, attrs }">
      <v-btn block color="primary" dark v-bind="attrs" v-on="on">
        {{ role_id == 2 ? "New Agent" : "New Guide" }}
      </v-btn>
    </template>
    <v-card>
      <v-card-title>
        <span class="headline">
          {{ role_id == 2 ? "New Agent" : "New Guide" }}
        </span>
      </v-card-title>
      <v-card-text>
        <v-container>
          <v-row>
            <v-col cols="12">
              <v-form ref="form" v-model="valid" :lazy-validation="lazy">
                <v-text-field
                  v-model="fname"
                  :rules="requiredRules"
                  label="First Name"
                  required
                />
                <v-text-field
                  v-model="lname"
                  :rules="requiredRules"
                  label="Last Name"
                />
                <v-text-field
                  v-model="email"
                  label="Email"
                  :rules="emailRules"
                  required
                />
                <v-text-field
                  v-model="twilio_phone"
                  label="Twilio Phone"
                  required
                  counter="13"
                  v-mask="'(###)###-####'"
                />
                <v-autocomplete
                  v-model="selectedSubroles"
                  :items="subroles"
                  item-text="title"
                  item-value="id"
                  persistent-hint
                  label="Choose sub-roles"
                  multiple
                >
                  <template v-slot:prepend-item>
                    <v-list-item ripple @click="changeAllSubrolesHandler">
                      <v-list-item-action>
                        <v-icon
                          :color="selectedSubroles > 0 ? 'indigo darken-4' : ''"
                        >
                          {{ icon }}
                        </v-icon>
                      </v-list-item-action>
                      <v-list-item-content>
                        <v-list-item-title>Select All</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-divider class="mt-2"></v-divider>
                  </template>

                  <template v-slot:selection="{ item, index }">
                    <v-chip small v-if="index == 0">
                      <span>{{ item.title }}</span>
                    </v-chip>
                    <span v-if="index == 1" class="grey--text caption">
                      (+{{ selectedSubroles.length - 1 }} others sub-roles)
                    </span>
                  </template>
                </v-autocomplete>
                <v-autocomplete
                  v-if="role_id != 3"
                  v-model="selectedStates"
                  :items="baseData.states"
                  item-text="name"
                  item-value="code"
                  persistent-hint
                  label="Choose states"
                  multiple
                >
                  <template v-slot:prepend-item>
                    <v-list-item ripple @click="changeAllStatesHandler">
                      <v-list-item-action>
                        <v-icon
                          :color="
                            selectedStates.length > 0 ? 'indigo darken-4' : ''
                          "
                        >
                          {{ icon }}
                        </v-icon>
                      </v-list-item-action>
                      <v-list-item-content>
                        <v-list-item-title>Select All</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-divider class="mt-2"></v-divider>
                  </template>

                  <template v-slot:selection="{ item, index }">
                    <v-chip small v-if="index == 0">
                      <span>{{ item.name }}</span>
                    </v-chip>
                    <span v-if="index == 1" class="grey--text caption">
                      (+{{ selectedStates.length - 1 }} others states)
                    </span>
                  </template>
                </v-autocomplete>
                <v-text-field
                  v-model="password"
                  label="Password"
                  @input="generatePassword = ''"
                  type="password"
                  :rules="requiredRules"
                  required
                />
                <v-text-field
                  v-model="pwdConfirm"
                  label="Re-Enter Password"
                  type="password"
                  :rules="pwdConfirmRules"
                  required
                />
                <div class="justify-center">
                  <h3 class="mb-3">
                    Generated password: {{ generatePassword }}
                  </h3>
                  <v-btn class="success" small @click="generateRandomPassword">
                    Generate Password
                  </v-btn>
                </div>
                <div class="justify-center mt-3">
                  <h3 class="mb-3">Email credentials:</h3>
                </div>
                <v-text-field
                    v-model="clientId"
                    label="Client ID"
                />
                <v-text-field
                  v-model="clientSecret"
                  label="Client secret"
                />
                <v-text-field
                    v-model="refreshToken"
                    label="Refresh token"
                />
                <v-text-field
                    v-model="accessToken"
                    label="Access token"
                />
              </v-form>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="blue darken-1" text @click="closeHandler">Close</v-btn>
        <v-btn
          color="blue darken-1"
          :disabled="!valid"
          text
          @click="createAgent"
        >
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

export default {
  name: "NewAgentForm",
  props: {
    role_id: Number,
  },
  data() {
    return {
      dialog: false,
      selectedSubroles: [],
      fname: "",
      lname: "",
      email: "",
      twilio_phone: "",
      selectedStates: [],
      password: "",
      pwdConfirm: "",
      generatePassword: "",
      clientId: "",
      clientSecret: "",
      refreshToken: "",
      accessToken: "",
      allStates: false,
      allRoles: false,
      lazy: false,
      valid: false,
      requiredRules: [(value) => !!value || "Required."],
      emailRules: [
        (value) => !!value || "Required.",
        (v) =>
          /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(v) ||
          "Invalid Email address",
      ],
      pwdConfirmRules: [
        (value) => !!value || "Required.",
        (value) => value === this.password || "Password must be the same",
      ],
    };
  },
  mounted() {
    this.loadSubroles();
  },
  methods: {
    ...mapActions({
      signOutAction: "auth/signOut",
      showHandlerSnackbar: "snackbar/showHandlerSnackbar",
      loadSubroles: "agents/loadSubroles",
    }),

    closeHandler() {
      this.dialog = false;
      this.resetForm();
    },

    changeAllStatesHandler() {
      this.allStates = !this.allStates;

      if (this.allStates) {
        this.selectedStates = this.baseData.states.map((state) => state.code);
      } else this.selectedStates = [];
    },

    changeAllSubrolesHandler() {
      this.allRoles = !this.allRoles;

      if (this.allRoles) {
        this.selectedSubroles = this.subroles.map((roles) => roles.id);
      } else this.selectedSubroles = [];
    },

    generateRandomPassword() {
      this.generatePassword = Math.random().toString(36).substr(2, 12);
      this.password = this.generatePassword;
      this.pwdConfirm = this.generatePassword;
    },

    resetForm() {
      this.selectedSubroles = [];
      this.fname = "";
      this.lname = "";
      this.email = "";
      this.twilio_phone = null;
      this.selectedStates = [];
      this.password = "";
      this.pwdConfirm = "";
      this.clientId = "";
      this.clientSecret = "";
      this.refreshToken = "";
      this.accessToken = ""
    },

    createAgent() {
      const newAgent = {
        role_id: this.role_id,
        fname: this.fname,
        lname: this.lname,
        email: this.email,
        twilio_phone: this.twilio_phone,
        email_credentials: {
          clientId: this.clientId,
          clientSecret: this.clientSecret,
          refreshToken: this.refreshToken,
          accessToken: this.accessToken,
        },
        states: this.selectedStates,
        password: this.password,
        subroles: this.selectedSubroles,
      };

      this.$axios
        .post("/agents/create", newAgent)
        .then((res) => {
          this.showHandlerSnackbar({
            color: res.data.status,
            text: res.data.message,
            show: true,
          });
          this.resetForm();
        })
        .catch((e) => {
          this.showHandlerSnackbar({
            color: e.response.data.status,
            text: e.response.data.message,
            show: true,
          });
        });
    },
  },
  computed: {
    ...mapGetters({
      baseData: "baseData/baseData",
      subroles: "agents/subroles",
    }),
    selectedAllStates() {
      return this.selectedStates.length === this.baseData.states.length;
    },
    selectedAllSubroles() {
      return this.selectedSubroles.length === this.subroles.length;
    },
    selectedSomeStates() {
      return this.selectedStates.length > 0 && !this.selectedAllStates;
    },
    selectedSomeSubroles() {
      return this.selectedSubroles.length > 0 && !this.selectedSubroles;
    },
    icon() {
      if (this.selectedAllStates) return "mdi-close-box";
      if (this.selectedAllSubroles) return "mdi-close-box";
      if (this.selectedSomeStates) return "mdi-minus-box";
      if (this.selectedSomeSubroles) return "mdi-minus-box";
      return "mdi-checkbox-blank-outline";
    },
  },
};
</script>